/**
 * Variable Browser — Site → Category → Device → Metric (djin-cloud#806;
 * categories and type icons #954).
 *
 * The genieos original browsed a TDengine SCHEMA (supertable → device →
 * register). There is no TDengine here: the tree is sites from `GET /v1/sites`
 * plus a lazily-fetched per-site `GET /v1/historian/catalog/:siteId`. What
 * carried over is the chrome — header, search, scrollable tree, selection
 * footer — and the interaction model, not the data.
 *
 * ⚠️ Three things to leave alone:
 *
 * 1. THE ARIA TREE IS A CONTRACT, not decoration. The Historian E2E spec
 *    addresses this panel by `role="tree"` and its rows by `role="treeitem"`,
 *    and the `data-testid` handles are what it keys individual rows on
 *    (display text moves for copy reasons; ids do not). `role="group"` on the
 *    nested lists is what makes the structure walkable rather than a flat
 *    list of items that happen to be indented.
 *
 * 2. `variableId()` IS IMPORTED, never retyped. That template was written out
 *    three separate times before it was extracted; a divergence is silent —
 *    the row simply never deselects, with nothing in the console.
 *
 *    (#954 added the category row at aria-level 2, which moved grouped devices
 *    to level 3 and their metrics to 4. The site summary sits beside the
 *    groups, so it stays a level-2 device.)
 *
 * 3. A device with NO metrics is rendered greyed, not hidden. The catalog
 *    returns rostered devices with an empty `metrics` array deliberately, so
 *    "no data in 30 d" can be said out loud. A device that disappears from
 *    the tree reads as "not installed", which is a different claim.
 */

import { useEffect, useMemo, useRef, type RefObject } from 'react';
import {
  AlertTriangle,
  BatteryCharging,
  Building2,
  ChevronDown,
  ChevronRight,
  Circle,
  Cpu,
  Fuel,
  Gauge,
  LayoutDashboard,
  PlugZap,
  RefreshCw,
  Sun,
  type LucideIcon,
} from 'lucide-react';
import { Button, Card, Input } from '@/components/gamma';
import { cn } from '@/utils/cn';
import { useHistorian, variableId } from '@/contexts/HistorianContext';
import {
  buildSiteTree,
  deviceCategory,
  SITE_SUMMARY_TYPE,
  type DeviceCategory,
  type DeviceGroupNode,
  type DeviceNode,
  type MetricNode,
  type SiteNode,
} from './historianTree';
import { HISTORIAN_MAX_SERIES } from './historianConstants';
import { VariableBrowserSkeleton } from './VariableBrowserSkeleton';

/**
 * One icon per category. The icon replaced the type chip (#954): the chip was
 * `shrink-0`, so in a 320 px column the device LABEL was the only thing that
 * gave way — `BATTERY_STORAGE` left a BESS reading `BE…`.
 */
const CATEGORY_ICONS: Record<DeviceCategory, LucideIcon> = {
  battery: BatteryCharging,
  generator: Fuel,
  solar: Sun,
  meter: Gauge,
  ev: PlugZap,
  other: Cpu,
};

const deviceIcon = (d: DeviceNode): LucideIcon =>
  d.deviceType === SITE_SUMMARY_TYPE ? LayoutDashboard : CATEGORY_ICONS[deviceCategory(d.deviceType)];

export interface VariableBrowserProps {
  className?: string;
  /** Focus target for the page's search keyboard shortcut. */
  searchInputRef?: RefObject<HTMLInputElement>;
}

export function VariableBrowser({ className, searchInputRef }: VariableBrowserProps) {
  const {
    state,
    loadSites,
    loadCatalog,
    toggleSite,
    toggleDevice,
    toggleGroup,
    setSearchQuery,
    addVariable,
    removeVariable,
    canAddVariable,
    isSelected,
  } = useHistorian();

  /**
   * ONE automatic load attempt per mount, latched in a ref rather than
   * inferred from state.
   *
   * ⚠️ The obvious `if (sites.length === 0 && !sitesLoading) loadSites()` is
   * an UNBOUNDED RETRY, and it is not subtle: `SITES_ERROR`
   * (`HistorianContext.tsx`) clears `sitesLoading` and leaves `sites` empty,
   * so both conditions are true again AND both deps have returned to their
   * trigger values. Measured against the real reducer: 95 requests in 150 ms,
   * 426 in 500 ms, climbing until the tab is closed.
   *
   * Guarding on `!sitesError` fixes that one path and leaves its twin
   * untouched: a tenant whose `/v1/sites` legitimately returns ZERO sites
   * dispatches SITES_SUCCESS — no error, `sites.length` still 0 — and spins
   * exactly as hard. The latch is outcome-INDEPENDENT, which is the whole
   * reason it is a ref and not one more condition to get right.
   *
   * Retry is therefore an explicit user action: the Refresh button in the
   * header, or the one beside the error itself.
   */
  const autoLoadAttempted = useRef(false);
  useEffect(() => {
    if (autoLoadAttempted.current) return;
    // Someone else is already loading, or already did — not our attempt to make.
    if (state.sites.length > 0 || state.sitesLoading) return;
    autoLoadAttempted.current = true;
    void loadSites();
  }, [state.sites.length, state.sitesLoading, loadSites]);

  const { tree, autoExpand } = useMemo(
    () =>
      buildSiteTree({
        sites: state.sites,
        catalogs: state.catalogs,
        loading: state.catalogLoading,
        errors: state.catalogErrors,
        search: state.searchQuery,
      }),
    [state.sites, state.catalogs, state.catalogLoading, state.catalogErrors, state.searchQuery]
  );

  /**
   * A search hit auto-expands its own path WITHOUT dispatching a toggle —
   * expanding by mutation would leave the tree sprawled open once the search
   * is cleared, having silently rewritten expansion state the user owns.
   */
  const isOpen = (key: string, set: Set<string>) => set.has(key) || autoExpand.has(key);

  /**
   * A category starts COLLAPSED, which is what shortens the tree, except one
   * holding a single device, which starts OPEN: a header over one BESS is a
   * click that reveals nothing you could not already name. `toggledGroups`
   * records a flip from that default, so one set serves both.
   */
  const isGroupOpen = (g: DeviceGroupNode) =>
    autoExpand.has(g.key) || (g.devices.length === 1) !== state.toggledGroups.has(g.key);

  const onSite = (s: SiteNode) => {
    if (s.status === 'unloaded' || s.status === 'error') void loadCatalog(s.siteId);
    toggleSite(s.key);
  };

  const onMetric = (site: SiteNode, d: DeviceNode, m: MetricNode) => {
    if (isSelected(site.siteId, d.deviceId, m.name)) {
      removeVariable(variableId(site.siteId, d.deviceId, m.name));
      return;
    }
    addVariable({
      siteId: site.siteId,
      siteName: site.siteName,
      deviceId: d.deviceId,
      deviceLabel: d.label,
      metric: m.name,
      unit: m.unit,
    });
  };

  const renderDevice = (site: SiteNode, d: DeviceNode, level: number) => {
    const deviceOpen = isOpen(d.key, state.expandedDevices);
    const DeviceIcon = deviceIcon(d);
    return (
      <div key={d.key}>
        <button
          type="button"
          role="treeitem"
          aria-level={level}
          // Absent rather than `false` on a device that cannot
          // expand: `aria-expanded="false"` promises a subtree
          // that opening would not reveal.
          aria-expanded={d.hasData ? deviceOpen : undefined}
          // `aria-disabled`, NOT `disabled`. A native
          // `disabled` drops the row out of the tab order
          // entirely, so the one user who most needs to know
          // WHY it is unavailable — a keyboard user — is the
          // one who cannot reach it to hear "no data in 30 d".
          aria-disabled={!d.hasData || undefined}
          // The full name on hover: a long label can still truncate.
          title={d.hasData ? `${d.label} · ${d.deviceType}` : 'No data reported in the last 30 days'}
          data-testid={`historian-tree-device-${d.siteId}-${d.deviceId}`}
          className={cn(
            'w-full flex items-center gap-2 px-2 py-1 rounded text-left text-sm focus:outline-none focus:ring-2 focus:ring-ems-green/30',
            d.hasData
              ? 'hover:bg-ems-bg-card-hover'
              : 'opacity-50 cursor-not-allowed'
          )}
          onClick={() => {
            if (!d.hasData) return;
            toggleDevice(d.key);
          }}
        >
          <span className="text-ems-text-secondary" aria-hidden="true">
            {d.hasData ? (
              deviceOpen ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )
            ) : (
              <span className="inline-block w-[14px]" />
            )}
          </span>
          <DeviceIcon size={14} className="text-ems-blue shrink-0" aria-hidden="true" />
          {/* Label and ids stacked, so the label owns the row's width. */}
          <span className="flex flex-col min-w-0">
            <span className="text-ems-text-primary truncate">{d.label}</span>
            {d.subtitle && (
              <span className="text-xs text-ems-text-muted truncate">{d.subtitle}</span>
            )}
          </span>
          {d.unrostered && (
            <span
              data-testid="historian-device-unrostered"
              title="Telemetry arrives under this id, but no roster device claims it"
              className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-ems-amber/40 text-ems-amber shrink-0"
            >
              not in roster
            </span>
          )}
          {!d.hasData && (
            <span className="ml-auto text-xs text-ems-text-muted shrink-0">
              no data in 30 d
            </span>
          )}
        </button>

        {deviceOpen && d.metrics.length > 0 && (
          <div role="group" className="ml-6">
            {d.metrics.map((m) => {
              const selected = isSelected(site.siteId, d.deviceId, m.name);
              // At the cap, an already-selected row stays
              // live — otherwise reaching 12 series locks the
              // selection, including the rows you would
              // deselect to make room.
              const unavailable = !selected && !canAddVariable();
              return (
                <button
                  key={m.key}
                  type="button"
                  role="treeitem"
                  aria-level={level + 1}
                  aria-selected={selected}
                  // See the device row: `aria-disabled` keeps
                  // the row focusable so a keyboard user can
                  // discover that the 12-series cap is what
                  // is stopping them, rather than finding
                  // rows that silently cannot be tabbed to.
                  aria-disabled={unavailable || undefined}
                  title={
                    unavailable
                      ? `Deselect a variable first — the chart holds ${HISTORIAN_MAX_SERIES} series at most`
                      : undefined
                  }
                  data-testid={`historian-tree-metric-${m.siteId}-${m.deviceId}-${m.name}`}
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-1 rounded text-left text-sm focus:outline-none focus:ring-2 focus:ring-ems-green/30',
                    selected
                      ? 'bg-ems-green/10 text-ems-green'
                      : 'text-ems-text-secondary hover:bg-ems-bg-card-hover',
                    unavailable && 'opacity-40 cursor-not-allowed'
                  )}
                  onClick={() => {
                    if (unavailable) return;
                    onMetric(site, d, m);
                  }}
                >
                  <Circle
                    size={8}
                    className={cn('shrink-0', selected && 'fill-current')}
                    aria-hidden="true"
                  />
                  <span className="truncate">{m.name}</span>
                  <span className="ml-auto text-xs text-ems-text-muted shrink-0">
                    {m.unit ?? ''}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (state.sitesLoading && state.sites.length === 0) {
    return <VariableBrowserSkeleton className={className} />;
  }

  return (
    <Card
      variant="panel"
      className={cn('flex flex-col h-full min-h-0', className)}
      data-testid="historian-variable-browser"
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-ems-text-primary font-semibold text-sm">Variable Browser</h2>
        <Button
          variant="ghost"
          size="xs"
          onClick={() => void loadSites()}
          aria-label="Refresh sites"
          data-testid="historian-tree-refresh"
        >
          <RefreshCw size={14} />
        </Button>
      </div>

      <Input
        ref={searchInputRef}
        size="sm"
        placeholder="Search sites, devices, metrics..."
        value={state.searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        aria-label="Search sites, devices and metrics"
        data-testid="historian-tree-search"
      />

      {state.sitesError && (
        <div
          className="mt-2 flex items-center gap-1 text-xs text-ems-red"
          data-testid="historian-tree-sites-error"
          role="status"
        >
          <AlertTriangle size={12} aria-hidden="true" />
          <span className="truncate">{state.sitesError}</span>
          {/* The ONLY thing that re-requests the site list after a failure.
              Nothing re-arms automatically — see the mount effect above. */}
          <button
            type="button"
            className="underline shrink-0"
            data-testid="historian-tree-sites-retry"
            onClick={() => void loadSites()}
          >
            retry
          </button>
        </div>
      )}

      <div
        className="flex-1 min-h-0 overflow-y-auto mt-2"
        role="tree"
        aria-label="Sites, devices and metrics"
        aria-multiselectable="true"
        data-testid="historian-tree"
      >
        {tree.map((site) => {
          const siteOpen = isOpen(site.key, state.expandedSites);
          return (
            <div key={site.key} className="mb-0.5">
              <button
                type="button"
                role="treeitem"
                aria-level={1}
                aria-expanded={siteOpen}
                data-testid={`historian-tree-site-${site.siteId}`}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm hover:bg-ems-bg-card-hover focus:outline-none focus:ring-2 focus:ring-ems-green/30"
                onClick={() => onSite(site)}
              >
                <span className="text-ems-text-secondary" aria-hidden="true">
                  {siteOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
                <Building2 size={14} className="text-ems-green shrink-0" aria-hidden="true" />
                <span className="font-medium text-ems-text-primary truncate">{site.siteName}</span>
                <span className="ml-auto text-xs text-ems-text-muted shrink-0">
                  {site.status === 'loading'
                    ? 'loading…'
                    : site.status === 'loaded'
                      ? `${site.devices.length} ${site.devices.length === 1 ? 'device' : 'devices'}`
                      : ''}
                </span>
              </button>

              {(siteOpen || site.status === 'error') && (
                <div role="group" className="ml-4">
                  {site.status === 'error' && (
                    <div
                      className="flex items-center gap-1 px-2 py-1 text-xs text-ems-red"
                      data-testid={`historian-tree-site-error-${site.siteId}`}
                    >
                      <AlertTriangle size={12} aria-hidden="true" />
                      <span className="truncate">{site.error}</span>
                      <button
                        type="button"
                        className="underline shrink-0"
                        data-testid={`historian-tree-site-retry-${site.siteId}`}
                        onClick={() => void loadCatalog(site.siteId)}
                      >
                        retry
                      </button>
                    </div>
                  )}

                  {siteOpen && (
                    <>
                      {site.summary.map((d) => renderDevice(site, d, 2))}
                      {site.groups.map((g) => {
                        const groupOpen = isGroupOpen(g);
                        const GroupIcon = CATEGORY_ICONS[g.category];
                        return (
                          <div key={g.key}>
                            <button
                              type="button"
                              role="treeitem"
                              aria-level={2}
                              aria-expanded={groupOpen}
                              data-testid={`historian-tree-group-${site.siteId}-${g.category}`}
                              className="w-full flex items-center gap-2 px-2 py-1 rounded text-left text-sm hover:bg-ems-bg-card-hover focus:outline-none focus:ring-2 focus:ring-ems-green/30"
                              onClick={() => toggleGroup(g.key)}
                            >
                              <span className="text-ems-text-secondary" aria-hidden="true">
                                {groupOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              </span>
                              <GroupIcon size={14} className="text-ems-text-secondary shrink-0" aria-hidden="true" />
                              <span className="text-ems-text-secondary font-medium truncate">{g.label}</span>
                              <span className="ml-auto text-xs text-ems-text-muted shrink-0">
                                {g.devices.length}
                              </span>
                            </button>
                            {groupOpen && (
                              <div role="group" className="ml-4">
                                {g.devices.map((d) => renderDevice(site, d, 3))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {tree.length === 0 && (
          <p className="text-sm text-ems-text-muted p-2" data-testid="historian-tree-empty">
            {state.searchQuery ? 'No matches.' : 'No sites available.'}
          </p>
        )}
      </div>

      <div
        className="border-t border-ems-border pt-2 mt-2 text-xs text-ems-text-muted"
        data-testid="historian-selection-count"
      >
        {state.selectedVariables.length}/{HISTORIAN_MAX_SERIES} variables selected
      </div>
    </Card>
  );
}

export default VariableBrowser;
